import React, { Key, useEffect, useMemo, useState } from "react";
import { Button, Form, Row } from "antd";
import { useHistory } from "react-router";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import ToggleableVersionsTable from "../../../../components/ToggleableVersionsTable/ToggleableVersionsTable";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { TableColumn } from "../../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading, toDisplayFormat } from "../../../../lib/utils";
import { useOfferingGroupsContext } from "../../context/OfferingGroupsContext/OfferingGroupsContext";
import { IOfferingGroup } from "../../types/IOfferingGroup";
import { fetchOfferingGroupsVersions } from "../../store/slice/offeringGroupsSlice";
import { ReactComponent as CopyIcon } from "../../../../img/copy.svg";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ADMIN_OFFERING_GROUP_WRITE } from "../../../../lib/const/permissions";
import "./OfferingGroupsListTable.less";

interface TableData extends Partial<IOfferingGroup> {
  key?: Key;
  actions?: string;
  buttons?: string;
}

export const OfferingGroupsListTable = () => {
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const history = useHistory();

  const dispatch = useAppDispatch();

  const { handleOfferingGroupsListSearch } = useOfferingGroupsContext();

  const {
    offeringGroups,
    offeringGroupsLoading,
    offeringGroupsTypesDic,
    offeringGroupsVersions,
    offeringGroupsVersionsLoading,
    offeringGroupsPagination,
  } = useAppSelector((state) => state.offeringGroups);

  const [form] = Form.useForm();

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    name: "200px",
    groupingTypeId: "200px",
    // actionId: "100px",
    fromDate: "100px",
    toDate: "100px",
    // parameterTwo: "100px",
  };

  const getColumns = (): TableColumn<TableData>[] => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
    },
    {
      title: "Тип",
      dataIndex: "groupingTypeId",
      key: "groupingTypeId",
      width: columnsWidths.groupingTypeId,
      hidden: false,
      render: (typeId) =>
        versionsTableUtils.getDictionaryItemById(offeringGroupsTypesDic, typeId)?.name,
    },
    {
      title: "Период с",
      dataIndex: "fromDate",
      key: "fromDate",
      width: columnsWidths.fromDate,
      hidden: false,
      render: (fromDate) => toDisplayFormat(fromDate),
    },
    {
      title: "Период по",
      dataIndex: "toDate",
      key: "toDate",
      width: columnsWidths.toDate,
      hidden: false,
      render: (toDate) => toDisplayFormat(toDate),
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render(value, record, index) {
        return (
          <Row justify="space-between">
            <div />
            {hasAccess(MENU_ADMIN_OFFERING_GROUP_WRITE) && (
              <Button
                style={{
                  color: "#00BB77",
                  marginLeft: "15px",
                  border: "none",
                  background: "transparent",
                }}
                shape="circle"
                icon={<CopyIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(`/offering-groups/create/${record?.id}`);
                }}
              />
            )}
          </Row>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(Number(record.id));
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const getChildColumns = (): TableColumn<TableData>[] => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
    },
    {
      title: "Тип",
      dataIndex: "groupingTypeId",
      key: "groupingTypeId",
      width: columnsWidths.groupingTypeId,
      hidden: false,
      render: (typeId) =>
        versionsTableUtils.getDictionaryItemById(offeringGroupsTypesDic, typeId)?.name,
    },
    {
      title: "Период с",
      dataIndex: "fromDate",
      key: "fromDate",
      width: columnsWidths.fromDate,
      hidden: false,
      render: (fromDate) => toDisplayFormat(fromDate),
    },
    {
      title: "Период по",
      dataIndex: "toDate",
      key: "toDate",
      width: columnsWidths.toDate,
      hidden: false,
      render: (toDate) => toDisplayFormat(toDate),
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(Number(record.id));
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columns, setColumns] = useState<TableColumn<TableData>[]>(getColumns());

  const [childTableColumns, setChildTableColumns] = useState<TableColumn<TableData>[]>(
    getChildColumns()
  );

  // Обновление колонок
  useEffect(() => {
    setColumns(getColumns());
    setChildTableColumns(getChildColumns());
  }, [offeringGroupsTypesDic]);

  const tableData = useMemo((): TableData[] => {
    const data: TableData[] = offeringGroups.map(
      ({ id, name, groupingTypeId, fromDate, toDate }) => ({
        // actionId,
        // assocOffering,
        // assocOfferingGroup,
        // actorOffering,
        // actorOfferingGroup,
        key: id,
        id,
        name,
        groupingTypeId,
        fromDate,
        toDate,
        // actionId,
        // parameterOne: assocOffering.name || assocOfferingGroup.name,
        // parameterTwo: actorOffering.name || actorOfferingGroup.name,
      })
    );
    return data;
  }, [offeringGroups, offeringGroupsTypesDic]);

  const childTableData = useMemo((): TableData[] => {
    const data: TableData[] = offeringGroupsVersions.map(
      ({ id, name, groupingTypeId, fromDate, toDate }) => ({
        key: `${id}_${fromDate}`,
        id,
        name,
        groupingTypeId,
        fromDate,
        toDate,
      })
    );
    return data;
  }, [offeringGroupsVersions]);

  const onOpenVersion = (record: TableData, expanded: boolean) => {
    dispatch(fetchOfferingGroupsVersions(record.id!));
  };

  const onFinish = () => {};

  return (
    <div className="offering-groups-list-table">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="offering-groups-list-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childTableColumns}
          setChildTableColumns={setChildTableColumns}
          childTableData={childTableData}
          isTableLoading={checkIsLoading(offeringGroupsLoading)}
          isChildTableLoading={checkIsLoading(offeringGroupsVersionsLoading)}
          onOpenVersionsCallback={onOpenVersion}
          isPageable={{
            current: offeringGroupsPagination.current + 1,
            total: offeringGroupsPagination.total,
            pageSize: 7,
            showSizeChanger: false,
            onChange: (page: number) => {
              handleOfferingGroupsListSearch({ page: page - 1 });
            },
          }}
          onRow={(record) => ({
            onClick: () => {
              history.push(`/offering-groups/${record.id}`);
            },
          })}
        />
      </Form>

      <HistoryAndPropertyModal
        objectTypeSysname="entity_group"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_GROUP_WRITE)}
      />
    </div>
  );
};
